// The venue's tax mode and GSTIN, at the top of the Tax and invoices settings group (F-PAY-11; spec/13
// §13, spec/06 §10.3–§10.4): the mode in force, any scheduled change, and the history.
//
//  - Anyone who can open the venue's settings reads it (`venues:read`). Recording or withdrawing a
//    change is offered only when `GET /v1/me/access` says the member holds `tax:manage`; while that is
//    loading or cannot be read nothing is offered, and the server's `403` stays the enforcement.
//  - A change is dated — today or later in the venue's timezone, never earlier — and names the GSTIN a
//    registered mode needs. It changes which document every later order gets, so it is read back in
//    plain words, with the day it starts, before it is sent.
//  - A change dated later can be withdrawn until its day begins; one whose day has begun is permanent.
//
// The GSTIN's format and check character are the server's to judge (the contract's `GstinSchema`);
// the screen asks only that one is given when the mode needs it, and says a refusal in plain words.

import type { SaleTaxMode, TaxRegistration, TaxRegistrationStatus, TaxRegistrationView } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, FormSection, RadioGroup, Skeleton, TextField, useToast, type BadgeTone } from '@clavian/ui-web';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { fieldErrors, PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { useAccess } from '../api/queries';
import { LoadFailure } from '../shell/LoadFailure';
import { Facts, Notice, Section } from '../ui';
import { TAX_MANAGE, useScheduleTaxRegistration, useTaxRegistration, useWithdrawTaxRegistration } from './tax-registration';

const MODES: readonly SaleTaxMode[] = ['regular', 'composition', 'unregistered'];

const STATUS_TONE: Readonly<Record<TaxRegistrationStatus, BadgeTone>> = {
  in_force: 'ok',
  scheduled: 'info',
  withdrawn: 'neutral',
  superseded: 'neutral',
};

const needsGstin = (mode: SaleTaxMode): boolean => mode === 'regular' || mode === 'composition';

/** A business day (`YYYY-MM-DD`) as the venue's locale writes a date. It is a calendar day, not an instant. */
function useDayFormat(): (day: string) => string {
  const format = useVenueFormatter();
  return useMemo(() => {
    const formatter = new Intl.DateTimeFormat(format.locale, { dateStyle: 'medium', timeZone: 'UTC' });
    return (day: string) => formatter.format(new Date(`${day}T00:00:00Z`));
  }, [format.locale]);
}

export function TaxRegistrationGroup({ organizationId, venueId }: { readonly organizationId: string; readonly venueId: string }): ReactNode {
  const { t } = useTranslation('console');
  const access = useAccess({ venueId });
  const registration = useTaxRegistration(organizationId, venueId);
  const canManage = access.status === 'ready' && access.can(TAX_MANAGE);
  const [editing, setEditing] = useState(false);

  if (registration.data === undefined) {
    return (
      <Section title={t('taxRegistration.title')} tip={t('taxRegistration.hint')}>
        {registration.isError ? (
          <LoadFailure error={registration.error} message={t('taxRegistration.unavailable')} onRetry={() => void registration.refetch()} />
        ) : (
          <Skeleton shape="text" lines={3} />
        )}
      </Section>
    );
  }
  const view = registration.data;

  return (
    <>
      <Section
        title={t('taxRegistration.title')}
        tip={t('taxRegistration.hint')}
        actions={
          canManage && !editing ? (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              {t('taxRegistration.change')}
            </Button>
          ) : undefined
        }
      >
        <Current view={view} />
        {access.status === 'ready' && !canManage ? <Notice role="note">{t('taxRegistration.readOnly')}</Notice> : null}
        {editing && canManage ? (
          <ChangeForm organizationId={organizationId} venueId={venueId} view={view} onDone={() => setEditing(false)} />
        ) : null}
      </Section>
      {view.scheduled.length > 0 ? (
        <Scheduled organizationId={organizationId} venueId={venueId} changes={view.scheduled} canManage={canManage} />
      ) : null}
      <History changes={view.history} />
    </>
  );
}

function Current({ view }: { readonly view: TaxRegistrationView }): ReactNode {
  const { t } = useTranslation('console');
  const day = useDayFormat();
  const { current } = view;
  return (
    <>
      <Facts
        columns={3}
        items={[
          { key: 'mode', label: t('taxRegistration.current'), value: t(`taxRegistration.mode.${current.mode}`) },
          { key: 'gstin', label: t('taxRegistration.gstin'), value: current.gstin ?? t('taxRegistration.noGstin') },
          {
            key: 'since',
            label: t('taxRegistration.since'),
            value: current.effectiveOn === null ? t('taxRegistration.sinceDefault') : day(current.effectiveOn),
          },
        ]}
      />
      <p className="console-subtle">{t(`taxRegistration.modeHint.${current.mode}`)}</p>
      {current.source === 'default' ? <Notice role="note">{t('taxRegistration.defaultNote')}</Notice> : null}
    </>
  );
}

interface Draft {
  readonly mode: SaleTaxMode;
  readonly gstin: string;
  readonly effectiveOn: string;
  readonly reason: string;
}

type DraftErrors = Partial<Record<'mode' | 'gstin' | 'effectiveOn', string>>;

function ChangeForm({
  organizationId,
  venueId,
  view,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly view: TaxRegistrationView;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const toast = useToast();
  const day = useDayFormat();
  const schedule = useScheduleTaxRegistration(organizationId, venueId);
  const [draft, setDraft] = useState<Draft>({
    mode: view.current.mode,
    gstin: view.current.gstin ?? '',
    effectiveOn: view.businessDay,
    reason: '',
  });
  const [errors, setErrors] = useState<DraftErrors>({});
  const [failure, setFailure] = useState<string | undefined>(undefined);
  // The confirm step: a change of mode changes the document every later order gets.
  const [confirming, setConfirming] = useState(false);

  const gstin = draft.gstin.trim().toUpperCase();
  const set = (patch: Partial<Draft>) => setDraft((previous) => ({ ...previous, ...patch }));

  function review(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: DraftErrors = {};
    if (needsGstin(draft.mode) && gstin === '') next.gstin = t('taxRegistration.error.gstinRequired');
    if (draft.effectiveOn === '') next.effectiveOn = t('taxRegistration.error.effectiveOnRequired');
    // Business days compare as `YYYY-MM-DD` strings; the server checks again in the venue's timezone.
    else if (draft.effectiveOn < view.businessDay) next.effectiveOn = t('taxRegistration.error.effectiveInPast');
    const unchanged =
      view.scheduled.length === 0 && draft.mode === view.current.mode && (needsGstin(draft.mode) ? gstin : null) === view.current.gstin;
    if (unchanged) next.mode = t('taxRegistration.error.unchanged');
    setErrors(next);
    setFailure(undefined);
    if (Object.keys(next).length === 0) setConfirming(true);
  }

  function refused(error: unknown): void {
    setConfirming(false);
    const code = problemCode(error);
    if (code === PROBLEM.validationFailed) {
      const next: DraftErrors = {};
      for (const field of fieldErrors(error)) {
        if (field.field === 'gstin') {
          next.gstin =
            field.code === 'required'
              ? t('taxRegistration.error.gstinRequired')
              : field.code === 'not_allowed'
                ? t('taxRegistration.error.gstinNotAllowed')
                : t('taxRegistration.error.gstinInvalid');
        } else if (field.field === 'effectiveOn') {
          next.effectiveOn = t('taxRegistration.error.effectiveOnRequired');
        }
      }
      if (Object.keys(next).length > 0) {
        setErrors(next);
        return;
      }
    }
    // A tax invoice or a bill of supply is issued under the organisation's legal name and registered address
    // (ADR-0046): this screen says where an owner records them. A conflict is a date already begun; the rest (not
    // permitted, a code with no copy) is the shared copy.
    setFailure(
      code === PROBLEM.businessDetailsRequired
        ? t('taxRegistration.error.businessDetailsRequired')
        : code === PROBLEM.conflict
          ? t('taxRegistration.error.effectiveInPast')
          : problem.text(error, { fallback: t('taxRegistration.error.failed') }),
    );
  }

  function confirm(): void {
    const reason = draft.reason.trim();
    schedule.mutate(
      {
        mode: draft.mode,
        effectiveOn: draft.effectiveOn,
        ...(needsGstin(draft.mode) ? { gstin } : {}),
        ...(reason === '' ? {} : { reason }),
      },
      {
        onSuccess: () => {
          toast.show({ title: t('taxRegistration.recorded', { mode: t(`taxRegistration.mode.${draft.mode}`), date: day(draft.effectiveOn) }), tone: 'ok' });
          onDone();
        },
        onError: refused,
      },
    );
  }

  if (confirming) {
    const mode = t(`taxRegistration.mode.${draft.mode}`);
    const date = day(draft.effectiveOn);
    return (
      <div className="console-form">
        <Notice tone="warn" role="alert" title={t('taxRegistration.confirm.title', { mode, date })}>
          <p>{t(`taxRegistration.confirm.effect.${draft.mode}`, { gstin })}</p>
          <p>{t('taxRegistration.confirm.keeps')}</p>
          <p>{draft.effectiveOn === view.businessDay ? t('taxRegistration.confirm.today') : t('taxRegistration.confirm.later')}</p>
        </Notice>
        <div className="console-form__actions">
          <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
            {t('common.back')}
          </Button>
          <Button type="button" variant="primary" busy={schedule.isPending} onClick={confirm}>
            {t('taxRegistration.confirm.submit')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="console-form" aria-label={t('taxRegistration.form.label')} noValidate onSubmit={review}>
      {failure !== undefined ? (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      ) : null}
      {/* Three fields (the mode, its GSTIN, the day); the audit-log reason waits behind More options. */}
      <FormSection
        more={
          <TextField
            label={t('taxRegistration.form.reason')}
            description={t('taxRegistration.form.reasonHint')}
            showOptional
            maxLength={500}
            value={draft.reason}
            onChange={(event) => set({ reason: event.target.value })}
          />
        }
      >
      <RadioGroup
        label={t('taxRegistration.form.mode')}
        value={draft.mode}
        onValueChange={(value) => set({ mode: value as SaleTaxMode })}
        error={errors.mode}
        options={MODES.map((mode) => ({ value: mode, label: t(`taxRegistration.mode.${mode}`) }))}
        description={t(`taxRegistration.modeHint.${draft.mode}`)}
      />
      {needsGstin(draft.mode) ? (
        <TextField
          label={t('taxRegistration.form.gstin')}
          description={t('taxRegistration.form.gstinHint')}
          required
          autoComplete="off"
          spellCheck={false}
          maxLength={15}
          value={draft.gstin}
          onChange={(event) => set({ gstin: event.target.value })}
          error={errors.gstin}
        />
      ) : null}
      <TextField
        label={t('taxRegistration.form.effectiveOn')}
        description={t('taxRegistration.form.effectiveOnHint')}
        required
        type="date"
        min={view.businessDay}
        value={draft.effectiveOn}
        onChange={(event) => set({ effectiveOn: event.target.value })}
        error={errors.effectiveOn}
      />
      </FormSection>
      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary">
          {t('taxRegistration.form.review')}
        </Button>
      </div>
    </form>
  );
}

function Scheduled({
  organizationId,
  venueId,
  changes,
  canManage,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly changes: readonly TaxRegistration[];
  readonly canManage: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const toast = useToast();
  const day = useDayFormat();
  const withdraw = useWithdrawTaxRegistration(organizationId, venueId);
  const [confirming, setConfirming] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  function send(change: TaxRegistration): void {
    const words = { mode: t(`taxRegistration.mode.${change.mode}`), date: day(change.effectiveOn) };
    withdraw.mutate(change.id, {
      onSuccess: () => {
        toast.show({ title: t('taxRegistration.withdrawn', words), tone: 'ok' });
        setConfirming(undefined);
        setFailure(undefined);
      },
      onError: (error) => {
        setConfirming(undefined);
        setFailure(problemCode(error) === PROBLEM.conflict ? t('taxRegistration.error.withdrawBegun') : problem.text(error, { fallback: t('taxRegistration.error.withdrawFailed') }));
      },
    });
  }

  return (
    <Section title={t('taxRegistration.scheduledTitle')} tip={t('taxRegistration.scheduledHint')}>
      {failure !== undefined ? (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      ) : null}
      <ul className="console-tax-changes" aria-label={t('taxRegistration.scheduledList')}>
        {changes.map((change) => {
          const words = { mode: t(`taxRegistration.mode.${change.mode}`), date: day(change.effectiveOn) };
          return (
            <li key={change.id} className="console-tax-changes__item">
              <p>
                <strong>{t('taxRegistration.scheduledItem', words)}</strong>
                {change.gstin === null ? null : <span className="console-subtle"> · {change.gstin}</span>}
              </p>
              {change.reason === null ? null : <p className="console-subtle">{change.reason}</p>}
              {canManage && confirming === change.id ? (
                <div className="console-form">
                  <Notice tone="warn" role="alert">
                    {t('taxRegistration.withdrawConfirm', words)}
                  </Notice>
                  <div className="console-form__actions">
                    <Button type="button" variant="ghost" onClick={() => setConfirming(undefined)}>
                      {t('common.back')}
                    </Button>
                    <Button type="button" variant="danger" busy={withdraw.isPending} onClick={() => send(change)}>
                      {t('taxRegistration.withdrawSubmit')}
                    </Button>
                  </div>
                </div>
              ) : canManage ? (
                <div className="console-form__actions">
                  <Button variant="ghost" size="sm" aria-label={t('taxRegistration.withdrawFor', words)} onClick={() => setConfirming(change.id)}>
                    {t('taxRegistration.withdraw')}
                  </Button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

function History({ changes }: { readonly changes: readonly TaxRegistration[] }): ReactNode {
  const { t } = useTranslation('console');
  const day = useDayFormat();
  // A list rather than a table: five columns do not fit a phone, and a table that scrolls sideways is
  // a region the keyboard cannot reach.
  return (
    <Section title={t('taxRegistration.historyTitle')}>
      {changes.length === 0 ? (
        <p className="console-subtle">{t('taxRegistration.historyEmpty')}</p>
      ) : (
        <ul className="console-tax-changes" aria-label={t('taxRegistration.historyCaption')}>
          {changes.map((change) => (
            <li key={change.id} className="console-tax-changes__item">
              <p>
                <strong>{t('taxRegistration.scheduledItem', { mode: t(`taxRegistration.mode.${change.mode}`), date: day(change.effectiveOn) })}</strong>{' '}
                <Badge tone={STATUS_TONE[change.status]} dot>
                  {t(`taxRegistration.status.${change.status}`)}
                </Badge>
              </p>
              <p className="console-subtle">
                {t('taxRegistration.gstin')}: {change.gstin ?? t('taxRegistration.noGstin')}
                {change.reason === null ? null : <> · {change.reason}</>}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
